import { HStack, Menu, Portal } from '@chakra-ui/react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { closePopup, openPopup } from '../../data/providers/popupsProvider';
import {
  authenticatedUserProvider,
  unauthenticate,
} from '../../data/providers/userProvider';
import { IPermission, IUser } from '../../data/types';
import { KEYS } from '../../keys';
import { Activity } from '../sections/activity/Activity';
import { ManageArchive } from '../sections/archive/ManageArchive';

import { AccountSettingsPopup } from '../settings/account/AccountSettings';
import { Integrations } from '../settings/integrations/Integrations';
import { ManageUsers } from '../settings/manage_users/ManageUsers';
import { MetricSettings } from '../settings/metrics/MetricSettings';
import { OrgOptionsPopup } from '../settings/org_options/OrgOptionsPopup';
import { Tags } from '../settings/tags/Tags';
import { WizardContainer } from '../pages/device-updater/components/WizardContainer';
import { Brand } from '../shared/Brand';
import { Icon } from '../shared/Icon';
import { SegmentGroup } from '../shared/SegmentGroup';
import { NavLinks } from './NavLinks';

const NavWrapper = styled.nav`
  height: 60px;
  position: fixed;
  top: 5px;
  right: 0;
  left: 0;
  z-index: 10;
  color: var(--color-menu-text);
  margin: 0px 20px;
  padding: 0px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;

  @media (max-width: 991px) {
    margin-left: 10px;
    margin-right: 10px;
  }
`;

const MenuContainer = styled.div`
  display: flex;
  flex: 9;
  margin: 0;
  padding: 0px 10px;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  width: 100%;
  padding-right: 0;
`;

export const NavItemTitle = styled.span`
  margin-left: 3px;

  @media (max-width: 991px) {
    display: none;
  }
`;

const isTeamAdmin = (user: IUser): boolean => {
  return user?.permissions?.reduce(
    (acc: boolean, curr: IPermission): boolean => {
      if (acc) return acc;
      return curr.write === true;
    },
    false
  );
};

export const PageHeader = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const [isOrgAdmin, setIsOrgAdmin] = React.useState<boolean>(false);
    const [canManagerUsers, setCanManageUsers] = React.useState<boolean>(false);
    const [useNavDropdown, setUseNavDropdown] = React.useState<boolean>(
      window.innerWidth <= 600
    );
    const [segmentKey, setSegmentKey] = React.useState<number>(0);

    React.useEffect((): (() => void) => {
      const handleResize = (): void => {
        setUseNavDropdown(window.innerWidth <= 600);
      };
      window.addEventListener('resize', handleResize);
      return (): void => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect((): (() => void) => {
      const subscriber = authenticatedUserProvider().subscribe(
        (user: IUser): void => {
          setIsOrgAdmin(user.claims?.orgAdmin);
          setCanManageUsers(user.claims?.orgAdmin ?? isTeamAdmin(user));
        }
      );
      return (): void => subscriber.unsubscribe();
    }, []);

    const onClose = closePopup(props.history);

    const popupConfig: Record<
      string,
      { component: React.ReactElement; route: string }
    > = {
      live: { component: <Activity onClose={onClose} />, route: 'live' },
      metrics: {
        component: <MetricSettings onClose={onClose} />,
        route: 'metrics',
      },
      tags: { component: <Tags onClose={onClose} />, route: 'tags' },
      'org-options': {
        component: <OrgOptionsPopup onClose={onClose} />,
        route: 'org-options',
      },
      integrations: {
        component: <Integrations onClose={onClose} />,
        route: 'integrations',
      },
      'manage-users': {
        component: (
          <ManageUsers onClose={onClose} onSelect={null} selected={false} />
        ),
        route: 'manage-users',
      },
      'device-updater': {
        component: <WizardContainer onClose={onClose} />,
        route: 'updater',
      },
      archive: {
        component: <ManageArchive onClose={onClose} />,
        route: 'archive',
      },
      account: {
        component: <AccountSettingsPopup onClose={onClose} />,
        route: 'account-settings',
      },
    };

    const handleAction = (value: string): void => {
      if (value === 'scoreboard') {
        window.open('/scoreboard', '_blank', 'width=1920,height=1080');
        setSegmentKey((prev: number): number => prev + 1);
        return;
      }
      if (value === 'support') {
        window.open('https://www.hawkindynamics.com/support', '_blank');
        setSegmentKey((prev: number): number => prev + 1);
        return;
      }
      if (value === 'logout') {
        unauthenticate();
        return;
      }
      const config = popupConfig[value];
      if (config) {
        openPopup(config.component, props.history, config.route);
      }
    };

    const segmentItems = [
      {
        value: 'live',
        label: (
          <>
            <Icon name='timeline' size='small' /> Live Testing
          </>
        ),
      },
      {
        value: 'scoreboard',
        label: (
          <>
            <Icon name='poll' size='small' /> Scoreboard
          </>
        ),
      },
      {
        value: 'support',
        label: (
          <>
            <Icon name='help_center' size='small' /> Support
          </>
        ),
      },
    ];

    return (
      <NavWrapper role='navigation' aria-label='Secondary Menu'>
        <Brand
          onClick={(): void => props.history.push('/')}
          alt='Hawkin Dynamics Logo'
        />
        <MenuContainer id='secondary-menu'>
          {useNavDropdown ? (
            <Menu.Root
              onSelect={(details: { value: string }): void =>
                handleAction(details.value)
              }
              positioning={{ placement: 'bottom-end' }}
            >
              <Menu.Trigger
                display='flex'
                alignItems='center'
                justifyContent='center'
                gap='4px'
                bg='menuBackground'
                border='1px solid'
                borderColor='lightText'
                borderRadius='6px'
                color='menuText'
                cursor='pointer'
                padding='6px 10px'
                marginRight='8px'
                whiteSpace='nowrap'
                _hover={{ bg: 'menuHoverBackground' }}
              >
                <Icon name='apps' size='small' />
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content
                    bg='menuBackground'
                    border='1px solid'
                    borderColor='lightText'
                    borderRadius='8px'
                    padding='0.5rem'
                    minWidth='200px'
                    css={{
                      '& [data-part="item"]': {
                        borderRadius: '4px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        _hover: {
                          background: 'menuHoverBackground',
                        },
                      },
                    }}
                  >
                    <Menu.Item value='live'>
                      <Icon name='timeline' size='small' />
                      Live Testing
                    </Menu.Item>
                    <Menu.Item value='scoreboard'>
                      <Icon name='poll' size='small' />
                      Scoreboard
                    </Menu.Item>
                    <Menu.Item value='support'>
                      <Icon name='help_center' size='small' />
                      Support
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          ) : (
            <SegmentGroup
              key={segmentKey}
              items={segmentItems}
              onValueChange={(details: { value: string }): void =>
                handleAction(details.value)
              }
              size='sm'
              showIndicator={false}
            />
          )}
          <Menu.Root
            onSelect={(details: { value: string }): void =>
              handleAction(details.value)
            }
            positioning={{ placement: 'bottom-end' }}
          >
            <Menu.Trigger
              display='flex'
              alignItems='center'
              justifyContent='center'
              gap='4px'
              bg='menuBackground'
              border='1px solid'
              borderColor='lightText'
              borderRadius='6px'
              color='menuText'
              cursor='pointer'
              padding='6px 10px'
              marginLeft='8px'
              whiteSpace='nowrap'
              _hover={{ bg: 'menuHoverBackground' }}
            >
              <HStack>
                <Icon name='settings' size='small' />
                <NavItemTitle>Settings</NavItemTitle>
              </HStack>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  bg='menuBackground'
                  border='1px solid'
                  borderColor='lightText'
                  borderRadius='8px'
                  padding='0.5rem'
                  minWidth='200px'
                  css={{
                    '& [data-part="item"]': {
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      _hover: {
                        background: 'menuHoverBackground',
                      },
                    },
                  }}
                >
                  <Menu.Item value='metrics'>
                    <Icon name='checklist' size='small' />
                    Metric Settings
                  </Menu.Item>
                  <Menu.Item value='tags'>
                    <Icon name='sell' size='small' />
                    Tags
                  </Menu.Item>
                  {isOrgAdmin && (
                    <>
                      <Menu.Item value='org-options'>
                        <Icon name='build' size='small' />
                        Organization Options
                      </Menu.Item>
                      <Menu.Item value='integrations'>
                        <Icon name='integration_instructions' size='small' />
                        Integrations
                      </Menu.Item>
                    </>
                  )}
                  {canManagerUsers && (
                    <Menu.Item value='manage-users'>
                      <Icon name='manage_accounts' size='small' />
                      Manage Users
                    </Menu.Item>
                  )}
                  <Menu.Item
                    value='device-updater'
                    data-testid={KEYS.DEVICE_UPDATER_MENU_ITEM}
                  >
                    <Icon name='devices' size='small' />
                    Device Updater
                  </Menu.Item>
                  <Menu.Item value='archive'>
                    <Icon name='archive' size='small' />
                    Manage Archive
                  </Menu.Item>
                  <Menu.Separator />
                  <Menu.Item value='account'>
                    <Icon name='account_circle' size='small' />
                    Account Settings
                  </Menu.Item>
                  <Menu.Item value='logout'>
                    <Icon name='exit_to_app' size='small' />
                    Log Out
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </MenuContainer>
      </NavWrapper>
    );
  }
);

export default PageHeader;
